import React, { useMemo, useEffect, useState } from "react";
// Material Styling
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { Box, FormControl, InputLabel } from "@mui/material";
import { TextFieldStyles } from "./TextField";
import classNames from "classnames";
// Formik imports
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import { PatternFormat } from "react-number-format";

interface IMaskTextProps {
  patternMask?: string; // Mask to apply to input i.e "+9 (999) 999-9999"
  hideMask?: boolean;
  InputProps?: any; // <- material input wrapper component
  inputProps?: any; // <- actual input element
  className?: string;
  onChangeDelay?: number;
  meta?: Partial<FieldMetaProps<any>>;
  id?: string; // Override use of input name for id assigned.
}

export type IMaskTextFieldProps = IMaskTextProps & TextFieldProps;

// MaskTextField COMPONENT
export const MaskTextField = (props: IMaskTextFieldProps): JSX.Element => {
  const {
    hideMask,
    label,
    name,
    onFocus,
    onBlur,
    onChange,
    value,
    onChangeDelay,
    InputProps,
    className,
    disabled,
    meta,
    id,
    patternMask,
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [typingTimer, setTypingTimer] = useState<any>(null);
  const [internalValue, setInternalValue] = useState<string | number>("");

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  // Clear timer on unmount of component.
  useEffect(() => () => clearTimeout(typingTimer), [typingTimer]);

  // If value is changed from outside, update internal controlled value
  useEffect(() => {
    // Debug.log('outside change: ', value)
    const newValue = value === undefined || value === null ? "" : value;
    setInternalValue(newValue as string | number);
  }, [value]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }
    }, 1);
  };

  // If value is modified by the input component, update the parent controller after a slight delay.
  const handleInputOnchange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (!event.target) {
      return;
    }
    const delay = onChangeDelay !== undefined ? onChangeDelay : 500;
    event.persist();
    // Debug.log('inside change: ', event.target.value)
    setInternalValue(event.target.value);
    // Reset timer onChange and restart it.
    if (typingTimer) clearTimeout(typingTimer);
    setTypingTimer(
      setTimeout(() => {
        // Fire off the supplied onChange from props and not the formik field one (it breaks as it wants an event and not a value)
        if (onChange && typeof onChange === "function") {
          onChange(event);
        }
      }, delay)
    );
  };

  const hasError = meta?.error ? true : false;

  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error",
        !label && "hideMuiLabel"
      )}
      sx={(theme) => TextFieldStyles(theme, null).root}
      error={hasError}
    >
      <div className={"input_control"}>
        {!!label && <InputLabel htmlFor={"id-" + name + "-mask"}>{label}</InputLabel>}
        <Box className={`${className ?? ""}`} sx={(theme) => TextFieldStyles(theme, null).mask}>
          <PatternFormat
            allowEmptyFormatting={true}
            format={patternMask}
            value={internalValue}
            id={"id-" + name + "-mask"}
            onChange={(event) => handleInputOnchange(event)}
            onBlur={(event: any) => {
              setTimeout(() => {
                handleInputOnchange(event);
                if (onBlur) onBlur(event);
              }, 0);
            }}
            disabled={disabled}
            name={name}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
        </Box>
      </div>
      <FieldError meta={meta} />
    </FormControl>
  );
};
